/**
 * Catalogo **fechado** de codigos de erro + formato RFC 9457 (problem details).
 *
 * Fonte: `docs/API.md §2.1` (formato) e `§2.2` (catalogo), com os 11 codigos de conector /
 * perfil absorvidos de `docs/CONTRACTS.md §7.3.1`.
 *
 * **O catalogo esta fechado em 106 codigos: 103 de erro + 3 avisos em `200`** (API §10.3,
 * CONTRACTS §7.3.1). Esse numero e contratual: `errors.test.ts` quebra se ele mudar. Codigo
 * novo entra por PR que atualiza o documento **antes** do codigo — foi assim que a v1.1
 * fechou as tres formas do FATAL 11 reusando `ERP_PROFILE_REQUIRED` em vez de criar um
 * decimo segundo codigo.
 *
 * Duas regras duras que este arquivo carrega junto com a tabela:
 * - `code` e a unica coisa contra a qual o cliente programa. `title`/`detail` sao para humanos
 *   e podem mudar sem breaking change (API §2.1).
 * - Recurso de outro titular responde **404, nunca 403** (REG-051) — 403 confirmaria a
 *   existencia do pedido/receita alheio. E por isso que `GUARDIANSHIP_REQUIRED` e 404.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Vocabulario da tabela
// ---------------------------------------------------------------------------

/**
 * Acao que a UI deve tomar automaticamente. As **5 canonicas** vem do preambulo do API §2.2:
 * `RETRY` (repetir com backoff), `REFRESH` (recarregar o recurso), `BLOCK` (erro terminal),
 * `FIELD` (destacar campo), `AUTH` (renovar sessao).
 *
 * As 3 restantes sao transcricao literal de linhas especificas da mesma tabela, que nao usam
 * nenhuma das cinco: `NONE` para os avisos em `200` que a tabela marca so como "aviso, nao
 * erro" (`STORE_AT_CAPACITY`, `RETURN_ITEM_NOT_RESTOCKABLE`), `IGNORE` para
 * `INGEST_OUT_OF_ORDER` ("ignorar", descarte contabilizado) e `DLQ` para
 * `INGEST_CONTRACT_BROKEN`. Nao foram inventadas: estao escritas na coluna "Acao".
 */
export const CLIENT_ACTION_VALUES = [
  'RETRY',
  'REFRESH',
  'BLOCK',
  'FIELD',
  'AUTH',
  'NONE',
  'IGNORE',
  'DLQ',
] as const;

export const ClientActionSchema = z.enum(CLIENT_ACTION_VALUES);
export type ClientAction = z.infer<typeof ClientActionSchema>;

/** Os agrupamentos da tabela do API §2.2, preservados para navegacao e para o gate de cobertura. */
export const ERROR_GROUP_VALUES = [
  'GENERICO', // Genericos e protocolo
  'AUTH', // Autenticacao e sessao
  'CATALOGO', // Catalogo, preco e regulatorio
  'ESTOQUE', // Estoque, carrinho e preco
  'CUPOM', // Cupom, promocao e fidelidade
  'ENTREGA', // Entrega, retirada e loja
  'PAGAMENTO', // Pagamento
  'PEDIDO', // Pedido, ruptura e devolucao
  'SAUDE', // Receita, saude e privacidade
  'INTEGRACAO', // Integracao e backoffice
  'CONECTOR', // Conector de ERP, mapeamento e perfil de instancia (v1.1)
] as const;

export const ErrorGroupSchema = z.enum(ERROR_GROUP_VALUES);
export type ErrorGroup = z.infer<typeof ErrorGroupSchema>;

/**
 * `ERRO` sai com `application/problem+json`; `AVISO` viaja **no corpo de uma resposta `200`**,
 * nao como erro (as 3 linhas marcadas `200*` no API §2.2).
 */
export const ERROR_KIND_VALUES = ['ERRO', 'AVISO'] as const;
export const ErrorKindSchema = z.enum(ERROR_KIND_VALUES);
export type ErrorKind = z.infer<typeof ErrorKindSchema>;

/**
 * Quem responde pelo campo faltante em `PROFILE_INCOMPLETE` — vai em `meta.owner`
 * (API §2.2, tabela de conector; ADR-0020, IMPLANTACAO §2.13).
 */
export const PROFILE_OWNER_VALUES = ['FAR', 'JUR', 'CON', 'RT'] as const;
export const ProfileOwnerSchema = z.enum(PROFILE_OWNER_VALUES);
export type ProfileOwner = z.infer<typeof ProfileOwnerSchema>;

/** Uma linha da tabela do API §2.2. */
export interface ErrorCatalogEntry {
  /** Codigo estavel de maquina. Igual a chave do catalogo — verificado em teste. */
  readonly codigo: string;
  /** Status HTTP. `200` nas 3 linhas de aviso. */
  readonly httpStatus: number;
  /**
   * Mensagem pt-BR ao usuario, exatamente como o documento a redige (`{n}`, `{v}`, `{data}`,
   * `{loja}`, `{campo}` sao placeholders preenchidos pela camada HTTP).
   * `null` nas linhas em que o documento escreve "—": erro de integracao que nunca chega ao
   * usuario final.
   */
  readonly mensagemUsuario: string | null;
  /** Coluna "Acao no cliente" do API §2.2. */
  readonly acaoSugerida: ClientAction;
  readonly grupo: ErrorGroup;
  readonly kind: ErrorKind;
}

// ---------------------------------------------------------------------------
// O catalogo — 106 linhas, na ordem do API §2.2
// ---------------------------------------------------------------------------

export const ERROR_CATALOG = {
  // --- Genericos e protocolo (15) ---
  VALIDATION_ERROR: {
    codigo: 'VALIDATION_ERROR',
    httpStatus: 422,
    mensagemUsuario: 'Confira os campos destacados.',
    acaoSugerida: 'FIELD',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  INVALID_CURSOR: {
    codigo: 'INVALID_CURSOR',
    httpStatus: 400,
    mensagemUsuario: 'A lista mudou. Vamos recarregar.',
    acaoSugerida: 'REFRESH',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  INVALID_SORT_FIELD: {
    codigo: 'INVALID_SORT_FIELD',
    httpStatus: 400,
    mensagemUsuario: 'Não é possível ordenar por esse campo.',
    acaoSugerida: 'BLOCK',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  UNAUTHENTICATED: {
    codigo: 'UNAUTHENTICATED',
    httpStatus: 401,
    mensagemUsuario: 'Sua sessão expirou. Entre novamente.',
    acaoSugerida: 'AUTH',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  FORBIDDEN: {
    codigo: 'FORBIDDEN',
    httpStatus: 403,
    mensagemUsuario: 'Você não tem permissão para esta ação.',
    acaoSugerida: 'BLOCK',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  NOT_FOUND: {
    codigo: 'NOT_FOUND',
    httpStatus: 404,
    mensagemUsuario: 'Não encontramos o que você procura.',
    acaoSugerida: 'BLOCK',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  METHOD_NOT_ALLOWED: {
    codigo: 'METHOD_NOT_ALLOWED',
    httpStatus: 405,
    mensagemUsuario: null,
    acaoSugerida: 'BLOCK',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  CONFLICT_VERSION: {
    codigo: 'CONFLICT_VERSION',
    httpStatus: 409,
    mensagemUsuario: 'Alguém alterou isso enquanto você editava.',
    acaoSugerida: 'REFRESH',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  PRECONDITION_REQUIRED: {
    codigo: 'PRECONDITION_REQUIRED',
    httpStatus: 428,
    mensagemUsuario: null, // erro de integracao: falta `If-Match` (API §4)
    acaoSugerida: 'BLOCK',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  PAYLOAD_TOO_LARGE: {
    codigo: 'PAYLOAD_TOO_LARGE',
    httpStatus: 413,
    mensagemUsuario: 'O arquivo é muito grande (máximo 10 MB).',
    acaoSugerida: 'FIELD',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  UNSUPPORTED_MEDIA_TYPE: {
    codigo: 'UNSUPPORTED_MEDIA_TYPE',
    httpStatus: 415,
    mensagemUsuario: 'Envie um arquivo JPG, PNG ou PDF.',
    acaoSugerida: 'FIELD',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  RATE_LIMITED: {
    codigo: 'RATE_LIMITED',
    httpStatus: 429,
    mensagemUsuario: 'Muitas tentativas. Aguarde alguns segundos.',
    acaoSugerida: 'RETRY', // respeitar `Retry-After`
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  INTERNAL_ERROR: {
    codigo: 'INTERNAL_ERROR',
    httpStatus: 500,
    // 500 nunca vaza stack, mensagem de driver nem nome de tabela (API §2.1).
    mensagemUsuario: 'Tivemos um problema. Já fomos avisados.',
    acaoSugerida: 'RETRY', // 1 vez
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  SERVICE_DEGRADED: {
    codigo: 'SERVICE_DEGRADED',
    httpStatus: 503,
    mensagemUsuario: 'Estamos com instabilidade. Tente em instantes.',
    acaoSugerida: 'RETRY',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },
  MAINTENANCE: {
    codigo: 'MAINTENANCE',
    httpStatus: 503,
    mensagemUsuario: 'Estamos em manutenção programada.',
    acaoSugerida: 'BLOCK',
    grupo: 'GENERICO',
    kind: 'ERRO',
  },

  // --- Autenticacao e sessao (7) ---
  OTP_INVALID: {
    codigo: 'OTP_INVALID',
    httpStatus: 422,
    mensagemUsuario: 'Código incorreto. Confira e tente de novo.',
    acaoSugerida: 'FIELD',
    grupo: 'AUTH',
    kind: 'ERRO',
  },
  OTP_EXPIRED: {
    codigo: 'OTP_EXPIRED',
    httpStatus: 410,
    mensagemUsuario: 'Esse código expirou. Peça um novo.',
    acaoSugerida: 'REFRESH',
    grupo: 'AUTH',
    kind: 'ERRO',
  },
  OTP_MAX_ATTEMPTS: {
    codigo: 'OTP_MAX_ATTEMPTS',
    httpStatus: 429,
    mensagemUsuario: 'Muitas tentativas. Aguarde 15 minutos.',
    acaoSugerida: 'BLOCK',
    grupo: 'AUTH',
    kind: 'ERRO',
  },
  OTP_CHANNEL_UNAVAILABLE: {
    codigo: 'OTP_CHANNEL_UNAVAILABLE',
    httpStatus: 503,
    mensagemUsuario: 'Não conseguimos enviar o SMS agora. Tente pelo WhatsApp.',
    acaoSugerida: 'BLOCK',
    grupo: 'AUTH',
    kind: 'ERRO',
  },
  PHONE_ALREADY_LINKED: {
    codigo: 'PHONE_ALREADY_LINKED',
    httpStatus: 409,
    mensagemUsuario: 'Este telefone já está em outra conta.',
    acaoSugerida: 'FIELD',
    grupo: 'AUTH',
    kind: 'ERRO',
  },
  SESSION_REVOKED: {
    codigo: 'SESSION_REVOKED',
    httpStatus: 401,
    mensagemUsuario: 'Sua sessão foi encerrada por segurança.',
    acaoSugerida: 'AUTH',
    grupo: 'AUTH',
    kind: 'ERRO',
  },
  STEP_UP_REQUIRED: {
    codigo: 'STEP_UP_REQUIRED',
    httpStatus: 401,
    // CONTRACTS §5.5: reautenticacao nos ultimos 10 min; no MVP em estorno acima do limite
    // e aprovacao de receita (O1).
    mensagemUsuario: 'Confirme sua identidade para continuar.',
    acaoSugerida: 'AUTH',
    grupo: 'AUTH',
    kind: 'ERRO',
  },

  // --- Catalogo, preco e regulatorio (10) ---
  PRODUCT_UNAVAILABLE_REGULATORY: {
    codigo: 'PRODUCT_UNAVAILABLE_REGULATORY',
    httpStatus: 404,
    mensagemUsuario: 'Este produto não está disponível para venda pelo app.',
    acaoSugerida: 'BLOCK',
    grupo: 'CATALOGO',
    kind: 'ERRO',
  },
  CONTROLLED_SUBSTANCE: {
    codigo: 'CONTROLLED_SUBSTANCE',
    httpStatus: 404, // REG-002: controlado e invisivel; 404 nao confirma existencia
    mensagemUsuario: 'Este medicamento só é vendido presencialmente, na loja.',
    acaoSugerida: 'BLOCK', // nunca exibir preco
    grupo: 'CATALOGO',
    kind: 'ERRO',
  },
  PRESCRIPTION_REQUIRED: {
    codigo: 'PRESCRIPTION_REQUIRED',
    httpStatus: 422,
    mensagemUsuario: 'Este item exige receita. Você vai apresentá-la na entrega ou na retirada.',
    acaoSugerida: 'BLOCK', // com CTA de reserva
    grupo: 'CATALOGO',
    kind: 'ERRO',
  },
  RETENTION_REQUIRED: {
    codigo: 'RETENTION_REQUIRED',
    httpStatus: 422,
    mensagemUsuario: 'Este item exige a receita física, que fica retida na farmácia.',
    acaoSugerida: 'BLOCK', // com CTA de reserva
    grupo: 'CATALOGO',
    kind: 'ERRO',
  },
  QUANTITY_LIMIT_EXCEEDED: {
    codigo: 'QUANTITY_LIMIT_EXCEEDED',
    httpStatus: 422, // canonico de `QUANTITY_LIMIT` (CONTRACTS §7.3)
    mensagemUsuario: 'Máximo de {n} unidades deste item por pedido.',
    acaoSugerida: 'FIELD',
    grupo: 'CATALOGO',
    kind: 'ERRO',
  },
  PRICE_ABOVE_CMED_CEILING: {
    codigo: 'PRICE_ABOVE_CMED_CEILING',
    httpStatus: 409, // REG-032: teto CMED e bloqueio, nunca aviso
    mensagemUsuario: 'Não conseguimos concluir. Nossa equipe já foi avisada.',
    acaoSugerida: 'BLOCK', // + alerta ao RT
    grupo: 'CATALOGO',
    kind: 'ERRO',
  },
  NBCAL_PROMOTION_FORBIDDEN: {
    codigo: 'NBCAL_PROMOTION_FORBIDDEN',
    httpStatus: 422, // Lei 11.265/2006
    mensagemUsuario: 'Este produto não participa de promoções.',
    acaoSugerida: 'BLOCK', // backoffice
    grupo: 'CATALOGO',
    kind: 'ERRO',
  },
  PROMOTION_NOT_ALLOWED_FOR_CLASS: {
    codigo: 'PROMOTION_NOT_ALLOWED_FOR_CLASS',
    // REG-030/031: mecanica fora da whitelist da classe. Nao confundir com
    // `COUPON_NOT_CUMULATIVE`, que e o canonico do eliminado `PROMOTION_EXCLUSIVE`.
    httpStatus: 422,
    mensagemUsuario: 'Esta oferta não vale para este tipo de produto.',
    acaoSugerida: 'BLOCK',
    grupo: 'CATALOGO',
    kind: 'ERRO',
  },
  INFORMATIVE_PRICE_NOT_CHARGEABLE: {
    codigo: 'INFORMATIVE_PRICE_NOT_CHARGEABLE',
    httpStatus: 422, // REG-081: preco informativo (PFPB/PBM) nunca entra no total
    mensagemUsuario: 'O valor do programa é confirmado na loja e não entra no total.',
    acaoSugerida: 'BLOCK',
    grupo: 'CATALOGO',
    kind: 'ERRO',
  },
  PRODUCT_NOT_PUBLISHED: {
    codigo: 'PRODUCT_NOT_PUBLISHED',
    httpStatus: 404,
    mensagemUsuario: 'Este produto ainda não está no nosso catálogo online.',
    acaoSugerida: 'BLOCK',
    grupo: 'CATALOGO',
    kind: 'ERRO',
  },

  // --- Estoque, carrinho e preco (9) ---
  ITEM_OUT_OF_STOCK: {
    codigo: 'ITEM_OUT_OF_STOCK',
    httpStatus: 409, // canonico de `INSUFFICIENT_STOCK` (CONTRACTS §7.3)
    mensagemUsuario: 'Um item ficou indisponível e foi retirado do carrinho.',
    acaoSugerida: 'REFRESH',
    grupo: 'ESTOQUE',
    kind: 'ERRO',
  },
  ITEM_LOW_STOCK_ADJUSTED: {
    codigo: 'ITEM_LOW_STOCK_ADJUSTED',
    httpStatus: 200, // aviso no corpo, nao erro
    mensagemUsuario: 'Ajustamos a quantidade: temos {n} unidades.',
    acaoSugerida: 'REFRESH',
    grupo: 'ESTOQUE',
    kind: 'AVISO',
  },
  INVENTORY_STALE: {
    codigo: 'INVENTORY_STALE',
    httpStatus: 409,
    mensagemUsuario:
      'A disponibilidade desta loja está desatualizada. Vamos confirmar por telefone.',
    acaoSugerida: 'REFRESH', // degrada para ON_REQUEST
    grupo: 'ESTOQUE',
    kind: 'ERRO',
  },
  PRICE_CHANGED: {
    codigo: 'PRICE_CHANGED',
    httpStatus: 409, // cotacao ainda valida, conteudo divergiu (CONTRACTS §6)
    mensagemUsuario: 'O preço de um item mudou. Confira antes de continuar.',
    acaoSugerida: 'REFRESH',
    grupo: 'ESTOQUE',
    kind: 'ERRO',
  },
  QUOTE_EXPIRED: {
    codigo: 'QUOTE_EXPIRED',
    httpStatus: 410, // TTL de 10 min (CONTRACTS §6); elimina QUOTE_STALE e PRICE_SNAPSHOT_EXPIRED
    mensagemUsuario: 'Sua cotação venceu. Vamos recalcular.',
    acaoSugerida: 'REFRESH',
    grupo: 'ESTOQUE',
    kind: 'ERRO',
  },
  CART_VERSION_CONFLICT: {
    codigo: 'CART_VERSION_CONFLICT',
    httpStatus: 409,
    mensagemUsuario: 'Seu carrinho foi alterado em outro dispositivo.',
    acaoSugerida: 'REFRESH',
    grupo: 'ESTOQUE',
    kind: 'ERRO',
  },
  CART_EMPTY: {
    codigo: 'CART_EMPTY',
    httpStatus: 422,
    mensagemUsuario: 'Seu carrinho está vazio.',
    acaoSugerida: 'BLOCK',
    grupo: 'ESTOQUE',
    kind: 'ERRO',
  },
  CART_STORE_MISMATCH: {
    codigo: 'CART_STORE_MISMATCH',
    httpStatus: 409, // canonico de `CART_STORE_CONFLICT` (CONTRACTS §7.3)
    mensagemUsuario: 'Você trocou de loja. Alguns itens podem mudar.',
    acaoSugerida: 'REFRESH',
    grupo: 'ESTOQUE',
    kind: 'ERRO',
  },
  MIN_ORDER_NOT_MET: {
    codigo: 'MIN_ORDER_NOT_MET',
    httpStatus: 422,
    mensagemUsuario: 'O pedido mínimo para esta zona é R$ {v}.',
    acaoSugerida: 'BLOCK',
    grupo: 'ESTOQUE',
    kind: 'ERRO',
  },

  // --- Cupom, promocao e fidelidade (10) ---
  COUPON_NOT_FOUND: {
    codigo: 'COUPON_NOT_FOUND',
    httpStatus: 404,
    mensagemUsuario: 'Cupom não encontrado.',
    acaoSugerida: 'FIELD',
    grupo: 'CUPOM',
    kind: 'ERRO',
  },
  COUPON_EXPIRED: {
    codigo: 'COUPON_EXPIRED',
    httpStatus: 422,
    mensagemUsuario: 'Este cupom venceu em {data}.',
    acaoSugerida: 'FIELD',
    grupo: 'CUPOM',
    kind: 'ERRO',
  },
  COUPON_NOT_STARTED: {
    codigo: 'COUPON_NOT_STARTED',
    httpStatus: 422,
    mensagemUsuario: 'Este cupom começa a valer em {data}.',
    acaoSugerida: 'FIELD',
    grupo: 'CUPOM',
    kind: 'ERRO',
  },
  COUPON_MIN_VALUE_NOT_MET: {
    codigo: 'COUPON_MIN_VALUE_NOT_MET',
    httpStatus: 422, // canonico de `COUPON_MIN_SUBTOTAL` (CONTRACTS §7.3)
    mensagemUsuario: 'Este cupom vale a partir de R$ {v}.',
    acaoSugerida: 'FIELD',
    grupo: 'CUPOM',
    kind: 'ERRO',
  },
  COUPON_NOT_APPLICABLE_ITEMS: {
    codigo: 'COUPON_NOT_APPLICABLE_ITEMS',
    httpStatus: 422, // canonico de `COUPON_NOT_APPLICABLE_TO_CLASS` (CONTRACTS §7.3)
    mensagemUsuario: 'Nenhum item do carrinho participa deste cupom.',
    acaoSugerida: 'FIELD',
    grupo: 'CUPOM',
    kind: 'ERRO',
  },
  COUPON_ALREADY_USED: {
    codigo: 'COUPON_ALREADY_USED',
    httpStatus: 409,
    mensagemUsuario: 'Você já usou este cupom.',
    acaoSugerida: 'FIELD',
    grupo: 'CUPOM',
    kind: 'ERRO',
  },
  COUPON_USAGE_LIMIT_REACHED: {
    codigo: 'COUPON_USAGE_LIMIT_REACHED',
    httpStatus: 409, // canonico de `COUPON_EXHAUSTED` (CONTRACTS §7.3)
    mensagemUsuario: 'Este cupom esgotou.',
    acaoSugerida: 'FIELD',
    grupo: 'CUPOM',
    kind: 'ERRO',
  },
  COUPON_NOT_CUMULATIVE: {
    codigo: 'COUPON_NOT_CUMULATIVE',
    httpStatus: 422, // canonico de `PROMOTION_EXCLUSIVE` (CONTRACTS §7.3)
    mensagemUsuario: 'Este cupom não pode ser somado à oferta já aplicada.',
    acaoSugerida: 'FIELD',
    grupo: 'CUPOM',
    kind: 'ERRO',
  },
  COUPON_STORE_RESTRICTED: {
    codigo: 'COUPON_STORE_RESTRICTED',
    httpStatus: 422, // canonico de `COUPON_STORE_NOT_ELIGIBLE` (CONTRACTS §7.3)
    mensagemUsuario: 'Este cupom vale só na {loja}.',
    acaoSugerida: 'FIELD',
    grupo: 'CUPOM',
    kind: 'ERRO',
  },
  LOYALTY_INSUFFICIENT_BALANCE: {
    codigo: 'LOYALTY_INSUFFICIENT_BALANCE',
    httpStatus: 422, // canonico de `LOYALTY_NEGATIVE_BALANCE` (CONTRACTS §7.3, RN-059)
    mensagemUsuario: 'Você não tem saldo suficiente.',
    acaoSugerida: 'BLOCK',
    grupo: 'CUPOM',
    kind: 'ERRO',
  },

  // --- Entrega, retirada e loja (9) ---
  OUT_OF_DELIVERY_AREA: {
    codigo: 'OUT_OF_DELIVERY_AREA',
    httpStatus: 422,
    mensagemUsuario: 'Ainda não entregamos nesse endereço. Você pode retirar na loja.',
    acaoSugerida: 'BLOCK', // + CTA retirada
    grupo: 'ENTREGA',
    kind: 'ERRO',
  },
  STORE_CLOSED: {
    codigo: 'STORE_CLOSED',
    httpStatus: 422,
    mensagemUsuario: 'A {loja} está fechada agora. Abre {horário}.',
    acaoSugerida: 'BLOCK', // + CTA agendar
    grupo: 'ENTREGA',
    kind: 'ERRO',
  },
  STORE_PAUSED: {
    codigo: 'STORE_PAUSED',
    httpStatus: 503,
    mensagemUsuario: 'A loja pausou os pedidos por alta demanda. Volte em {min} minutos.',
    acaoSugerida: 'RETRY',
    grupo: 'ENTREGA',
    kind: 'ERRO',
  },
  STORE_AT_CAPACITY: {
    codigo: 'STORE_AT_CAPACITY',
    httpStatus: 200,
    mensagemUsuario: 'Hoje está movimentado: previsão de {faixa}.',
    acaoSugerida: 'NONE', // a tabela escreve "aviso, nao erro"
    grupo: 'ENTREGA',
    kind: 'AVISO',
  },
  ADDRESS_REFERENCE_REQUIRED: {
    codigo: 'ADDRESS_REFERENCE_REQUIRED',
    httpStatus: 422, // R-28: `reference` e o campo que o motoboy usa; CEP nao dirige o formulario
    mensagemUsuario: 'Informe um ponto de referência — é o que o entregador usa para chegar.',
    acaoSugerida: 'FIELD',
    grupo: 'ENTREGA',
    kind: 'ERRO',
  },
  DELIVERY_SLOT_UNAVAILABLE: {
    codigo: 'DELIVERY_SLOT_UNAVAILABLE',
    httpStatus: 409,
    mensagemUsuario: 'Esse horário acabou de ser ocupado.',
    acaoSugerida: 'REFRESH',
    grupo: 'ENTREGA',
    kind: 'ERRO',
  },
  PICKUP_CODE_INVALID: {
    codigo: 'PICKUP_CODE_INVALID',
    httpStatus: 422,
    mensagemUsuario: 'Código de retirada inválido.',
    acaoSugerida: 'FIELD',
    grupo: 'ENTREGA',
    kind: 'ERRO',
  },
  PICKUP_EXPIRED: {
    codigo: 'PICKUP_EXPIRED',
    httpStatus: 410,
    mensagemUsuario: 'A reserva expirou. Refaça o pedido.',
    acaoSugerida: 'BLOCK',
    grupo: 'ENTREGA',
    kind: 'ERRO',
  },
  COURIER_TRAINING_EXPIRED: {
    codigo: 'COURIER_TRAINING_EXPIRED',
    httpStatus: 422, // REG-004: entregador treinado e condicao de despacho
    mensagemUsuario: 'Não é possível despachar: treinamento do entregador vencido.',
    acaoSugerida: 'BLOCK', // painel
    grupo: 'ENTREGA',
    kind: 'ERRO',
  },

  // --- Pagamento (9) ---
  PAYMENT_DECLINED: {
    codigo: 'PAYMENT_DECLINED',
    httpStatus: 402,
    mensagemUsuario: 'O pagamento não foi aprovado. Tente outro meio.',
    acaoSugerida: 'BLOCK',
    grupo: 'PAGAMENTO',
    kind: 'ERRO',
  },
  PAYMENT_METHOD_UNAVAILABLE: {
    codigo: 'PAYMENT_METHOD_UNAVAILABLE',
    httpStatus: 422, // tambem a recusa de CREDIARIO com FEATURE_STORE_CREDIT off (CONTRACTS §2.3)
    mensagemUsuario: 'Este meio de pagamento não está disponível para esta loja.',
    acaoSugerida: 'BLOCK',
    grupo: 'PAGAMENTO',
    kind: 'ERRO',
  },
  PIX_EXPIRED: {
    codigo: 'PIX_EXPIRED',
    httpStatus: 410,
    mensagemUsuario: 'O código Pix expirou. Vamos gerar outro.',
    acaoSugerida: 'REFRESH',
    grupo: 'PAGAMENTO',
    kind: 'ERRO',
  },
  PIX_ALREADY_PAID: {
    codigo: 'PIX_ALREADY_PAID',
    httpStatus: 409,
    mensagemUsuario: 'Este pedido já foi pago.',
    acaoSugerida: 'REFRESH',
    grupo: 'PAGAMENTO',
    kind: 'ERRO',
  },
  PIX_AMOUNT_MISMATCH: {
    codigo: 'PIX_AMOUNT_MISMATCH',
    httpStatus: 409,
    mensagemUsuario: 'O valor pago não confere. Nossa equipe vai te chamar.',
    acaoSugerida: 'BLOCK', // + incidente P1
    grupo: 'PAGAMENTO',
    kind: 'ERRO',
  },
  CASH_ON_DELIVERY_LIMIT: {
    codigo: 'CASH_ON_DELIVERY_LIMIT',
    httpStatus: 422, // `payment.cod.firstOrderLimitCents` (§10)
    mensagemUsuario: 'Para pagar na entrega, o limite é R$ {v} no primeiro pedido.',
    acaoSugerida: 'BLOCK',
    grupo: 'PAGAMENTO',
    kind: 'ERRO',
  },
  CHANGE_AMOUNT_INVALID: {
    codigo: 'CHANGE_AMOUNT_INVALID',
    httpStatus: 422, // `changeForCents` menor que o total
    mensagemUsuario: 'O valor do troco precisa ser maior que o total.',
    acaoSugerida: 'FIELD',
    grupo: 'PAGAMENTO',
    kind: 'ERRO',
  },
  REFUND_LIMIT_EXCEEDED: {
    codigo: 'REFUND_LIMIT_EXCEEDED',
    httpStatus: 409, // leva o Refund a APROVACAO_PENDENTE (CONTRACTS §2.2)
    mensagemUsuario: 'Estorno acima do limite diário. Aguarde aprovação do gerente.',
    acaoSugerida: 'BLOCK',
    grupo: 'PAGAMENTO',
    kind: 'ERRO',
  },
  PSP_UNAVAILABLE: {
    codigo: 'PSP_UNAVAILABLE',
    httpStatus: 503,
    mensagemUsuario: 'Nosso parceiro de pagamento está instável. Tente pagar na entrega.',
    acaoSugerida: 'RETRY', // + fallback
    grupo: 'PAGAMENTO',
    kind: 'ERRO',
  },

  // --- Pedido, ruptura e devolucao (7) ---
  ORDER_NOT_CANCELLABLE: {
    codigo: 'ORDER_NOT_CANCELLABLE',
    httpStatus: 409,
    mensagemUsuario: 'Este pedido já saiu para entrega e não pode ser cancelado pelo app.',
    acaoSugerida: 'BLOCK', // + CTA suporte
    grupo: 'PEDIDO',
    kind: 'ERRO',
  },
  ORDER_ALREADY_ACCEPTED: {
    codigo: 'ORDER_ALREADY_ACCEPTED',
    httpStatus: 409,
    mensagemUsuario: 'Outro atendente já assumiu este pedido.',
    acaoSugerida: 'REFRESH',
    grupo: 'PEDIDO',
    kind: 'ERRO',
  },
  ORDER_TRANSITION_INVALID: {
    codigo: 'ORDER_TRANSITION_INVALID',
    httpStatus: 422, // canonico de `ORDER_ILLEGAL_TRANSITION` (CONTRACTS §7.3, INV-O8/RN-085)
    mensagemUsuario: 'Ação indisponível para o estado atual do pedido.',
    acaoSugerida: 'REFRESH',
    grupo: 'PEDIDO',
    kind: 'ERRO',
  },
  ADJUSTMENT_PENDING: {
    codigo: 'ADJUSTMENT_PENDING',
    httpStatus: 409,
    mensagemUsuario: 'Existe um ajuste aguardando sua resposta.',
    acaoSugerida: 'REFRESH',
    grupo: 'PEDIDO',
    kind: 'ERRO',
  },
  ADJUSTMENT_EXPIRED: {
    codigo: 'ADJUSTMENT_EXPIRED',
    httpStatus: 410, // TTL de 10 min; aplica-se `OrderAdjustment.defaultOnTimeout` (X-17)
    mensagemUsuario: 'O prazo para responder ao ajuste terminou.',
    acaoSugerida: 'REFRESH',
    grupo: 'PEDIDO',
    kind: 'ERRO',
  },
  RETURN_WINDOW_CLOSED: {
    codigo: 'RETURN_WINDOW_CLOSED',
    httpStatus: 422, // CDC art. 49
    mensagemUsuario: 'O prazo de 7 dias para arrependimento terminou em {data}.',
    acaoSugerida: 'BLOCK',
    grupo: 'PEDIDO',
    kind: 'ERRO',
  },
  RETURN_ITEM_NOT_RESTOCKABLE: {
    codigo: 'RETURN_ITEM_NOT_RESTOCKABLE',
    httpStatus: 200, // aviso OBRIGATORIO — REG-060, RDC 222/2018
    mensagemUsuario:
      'Medicamento devolvido é descartado por norma sanitária. O reembolso é integral.',
    acaoSugerida: 'NONE', // a tabela escreve "aviso obrigatorio"
    grupo: 'PEDIDO',
    kind: 'AVISO',
  },

  // --- Receita, saude e privacidade (11) ---
  PRESCRIPTION_INVALID_FILE: {
    codigo: 'PRESCRIPTION_INVALID_FILE',
    httpStatus: 422,
    mensagemUsuario: 'Não conseguimos ler o arquivo. Envie uma foto nítida.',
    acaoSugerida: 'FIELD',
    grupo: 'SAUDE',
    kind: 'ERRO',
  },
  PRESCRIPTION_EXPIRED: {
    codigo: 'PRESCRIPTION_EXPIRED',
    httpStatus: 422,
    mensagemUsuario: 'Esta receita está fora da validade.',
    acaoSugerida: 'BLOCK',
    grupo: 'SAUDE',
    kind: 'ERRO',
  },
  PRESCRIPTION_NOT_APPROVED: {
    codigo: 'PRESCRIPTION_NOT_APPROVED',
    httpStatus: 422,
    mensagemUsuario: 'Sua receita ainda está em análise pelo farmacêutico.',
    acaoSugerida: 'BLOCK',
    grupo: 'SAUDE',
    kind: 'ERRO',
  },
  PRESCRIPTION_PATIENT_MISMATCH: {
    codigo: 'PRESCRIPTION_PATIENT_MISMATCH',
    httpStatus: 422, // canonico de `DIVERGENCIA_DE_PACIENTE` (CONTRACTS §7.3, RN-107) — REG-055
    mensagemUsuario: 'O paciente da receita não é o mesmo do pedido.',
    acaoSugerida: 'BLOCK',
    grupo: 'SAUDE',
    kind: 'ERRO',
  },
  PRESCRIPTION_QUANTITY_EXCEEDED: {
    codigo: 'PRESCRIPTION_QUANTITY_EXCEEDED',
    httpStatus: 422,
    mensagemUsuario: 'A quantidade pedida excede a receita.',
    acaoSugerida: 'FIELD',
    grupo: 'SAUDE',
    kind: 'ERRO',
  },
  PHARMACIST_ROLE_REQUIRED: {
    codigo: 'PHARMACIST_ROLE_REQUIRED',
    httpStatus: 403, // REG-020
    mensagemUsuario: 'Somente o farmacêutico responsável pode fazer isso.',
    acaoSugerida: 'BLOCK',
    grupo: 'SAUDE',
    kind: 'ERRO',
  },
  PHARMACIST_CRF_INACTIVE: {
    codigo: 'PHARMACIST_CRF_INACTIVE',
    httpStatus: 403,
    mensagemUsuario: 'CRF não verificado nos últimos 90 dias.',
    acaoSugerida: 'BLOCK',
    grupo: 'SAUDE',
    kind: 'ERRO',
  },
  CONSENT_REQUIRED: {
    codigo: 'CONSENT_REQUIRED',
    httpStatus: 403, // art. 11 LGPD
    mensagemUsuario: 'Precisamos da sua autorização para ativar isso.',
    acaoSugerida: 'BLOCK', // CTA para a tela de consentimento
    grupo: 'SAUDE',
    kind: 'ERRO',
  },
  CONSENT_REVOKED: {
    codigo: 'CONSENT_REVOKED',
    httpStatus: 403,
    mensagemUsuario: 'Você desativou esta função nas suas preferências.',
    acaoSugerida: 'BLOCK',
    grupo: 'SAUDE',
    kind: 'ERRO',
  },
  GUARDIANSHIP_REQUIRED: {
    codigo: 'GUARDIANSHIP_REQUIRED',
    // REG-051: recurso de outro titular e **404, nunca 403**. 403 confirmaria a existencia.
    httpStatus: 404,
    mensagemUsuario: 'Não encontramos o que você procura.',
    acaoSugerida: 'BLOCK',
    grupo: 'SAUDE',
    kind: 'ERRO',
  },
  DSAR_ALREADY_IN_PROGRESS: {
    codigo: 'DSAR_ALREADY_IN_PROGRESS',
    httpStatus: 409,
    mensagemUsuario: 'Já existe um pedido seu em andamento (protocolo {n}).',
    acaoSugerida: 'BLOCK',
    grupo: 'SAUDE',
    kind: 'ERRO',
  },

  // --- Integracao e backoffice (8) ---
  IDEMPOTENCY_KEY_REQUIRED: {
    codigo: 'IDEMPOTENCY_KEY_REQUIRED',
    httpStatus: 400,
    mensagemUsuario: null,
    acaoSugerida: 'BLOCK',
    grupo: 'INTEGRACAO',
    kind: 'ERRO',
  },
  IDEMPOTENCY_KEY_MISMATCH: {
    codigo: 'IDEMPOTENCY_KEY_MISMATCH',
    httpStatus: 422, // mesma chave, `requestFingerprint` diferente (X-08)
    mensagemUsuario: null,
    acaoSugerida: 'BLOCK',
    grupo: 'INTEGRACAO',
    kind: 'ERRO',
  },
  IDEMPOTENCY_IN_PROGRESS: {
    codigo: 'IDEMPOTENCY_IN_PROGRESS',
    httpStatus: 409, // status IN_PROGRESS na tabela de idempotencia
    mensagemUsuario: 'Estamos processando. Aguarde.',
    acaoSugerida: 'RETRY', // apos `Retry-After`
    grupo: 'INTEGRACAO',
    kind: 'ERRO',
  },
  WEBHOOK_SIGNATURE_INVALID: {
    codigo: 'WEBHOOK_SIGNATURE_INVALID',
    httpStatus: 401,
    mensagemUsuario: null,
    acaoSugerida: 'BLOCK',
    grupo: 'INTEGRACAO',
    kind: 'ERRO',
  },
  WEBHOOK_TIMESTAMP_SKEW: {
    codigo: 'WEBHOOK_TIMESTAMP_SKEW',
    httpStatus: 401,
    mensagemUsuario: null,
    acaoSugerida: 'BLOCK',
    grupo: 'INTEGRACAO',
    kind: 'ERRO',
  },
  ERP_UNAVAILABLE: {
    codigo: 'ERP_UNAVAILABLE',
    // NAO e `ERP_UNREACHABLE` (502). Este e a degradacao voltada ao CLIENTE FINAL: uma rota de
    // leitura nao confirmou dado com a loja e a UI mostra faixa/ON_REQUEST (API §2.2, nota).
    httpStatus: 503,
    mensagemUsuario: 'Não conseguimos confirmar com a loja agora.',
    acaoSugerida: 'RETRY',
    grupo: 'INTEGRACAO',
    kind: 'ERRO',
  },
  INGEST_OUT_OF_ORDER: {
    codigo: 'INGEST_OUT_OF_ORDER',
    httpStatus: 409, // evento com `version` menor que o aplicado — descarte contabilizado
    mensagemUsuario: null,
    acaoSugerida: 'IGNORE',
    grupo: 'INTEGRACAO',
    kind: 'ERRO',
  },
  INGEST_CONTRACT_BROKEN: {
    codigo: 'INGEST_CONTRACT_BROKEN',
    httpStatus: 422,
    mensagemUsuario: null, // + alarme imediato
    acaoSugerida: 'DLQ',
    grupo: 'INTEGRACAO',
    kind: 'ERRO',
  },

  // --- Conector de ERP, mapeamento e perfil de instancia (11) — CONTRACTS §7.3.1 ---
  // Regra dura destes 11: nenhum vaza nome de campo, tabela, coluna, consulta ou credencial do
  // ERP na resposta. A mensagem nomeia a CAPACIDADE afetada; o detalhe fica no log com
  // `clientSlug` e `correlationId`.
  PROFILE_INCOMPLETE: {
    codigo: 'PROFILE_INCOMPLETE',
    httpStatus: 409,
    mensagemUsuario:
      'Falta uma configuração desta farmácia para concluir: {campo}. Já avisamos quem responde por ela.',
    acaoSugerida: 'BLOCK', // NUNCA RETRY. `meta.missingKey` + `meta.owner` (ProfileOwner)
    grupo: 'CONECTOR',
    kind: 'ERRO',
  },
  ERP_UNREACHABLE: {
    codigo: 'ERP_UNREACHABLE',
    httpStatus: 502, // transitorio; alarme apos 3 ciclos
    mensagemUsuario: 'Não conseguimos falar com o sistema da loja agora.',
    acaoSugerida: 'RETRY',
    grupo: 'CONECTOR',
    kind: 'ERRO',
  },
  ERP_AUTH_FAILED: {
    codigo: 'ERP_AUTH_FAILED',
    httpStatus: 502, // permanente de contrato: DLQ + alarme P1. Retry nao resolve credencial
    mensagemUsuario: 'A conexão com o sistema da loja precisa ser reconfigurada.',
    acaoSugerida: 'BLOCK',
    grupo: 'CONECTOR',
    kind: 'ERRO',
  },
  ERP_SCHEMA_DRIFT: {
    codigo: 'ERP_SCHEMA_DRIFT',
    httpStatus: 502, // permanente de contrato: DLQ + alarme P1
    mensagemUsuario: 'O sistema da loja mudou de formato. Nossa equipe já foi avisada.',
    acaoSugerida: 'BLOCK',
    grupo: 'CONECTOR',
    kind: 'ERRO',
  },
  ERP_RATE_BUDGET_EXCEEDED: {
    codigo: 'ERP_RATE_BUDGET_EXCEEDED',
    httpStatus: 429,
    mensagemUsuario: 'Atingimos o limite de consultas do sistema da loja por hoje.',
    acaoSugerida: 'RETRY', // respeitando `Retry-After`; abre item de custo no painel de frota
    grupo: 'CONECTOR',
    kind: 'ERRO',
  },
  ERP_DB_USER_NOT_READONLY: {
    codigo: 'ERP_DB_USER_NOT_READONLY',
    httpStatus: 500, // recusa de BOOT — o `probe()` do perfil db-agent fez INSERT e NAO falhou
    mensagemUsuario: null,
    acaoSugerida: 'BLOCK',
    grupo: 'CONECTOR',
    kind: 'ERRO',
  },
  ERP_ADAPTER_NOT_CERTIFIED: {
    codigo: 'ERP_ADAPTER_NOT_CERTIFIED',
    httpStatus: 500, // recusa de BOOT — sem `certifiedAt` em NODE_ENV=production
    mensagemUsuario: null,
    acaoSugerida: 'BLOCK',
    grupo: 'CONECTOR',
    kind: 'ERRO',
  },
  ERP_PROFILE_REQUIRED: {
    codigo: 'ERP_PROFILE_REQUIRED',
    // Recusa de BOOT. **Tres formas, um defeito so** (FATAL 11 de IMPLANTACAO §2.13):
    // (a) `integration.profile` ausente; (b) `db-agent` sem `dbAccessAuthorizationRef`;
    // (c) D-19: chave/feature que exige uma ConnectorCapability que o adaptador nao publica
    // (tabela normativa das 11 linhas em CONTRACTS §2.14). A forma (c) REUSA este codigo —
    // e por isso que o catalogo continua fechado em 106.
    httpStatus: 500,
    mensagemUsuario: null,
    acaoSugerida: 'BLOCK',
    grupo: 'CONECTOR',
    kind: 'ERRO',
  },
  MAPPING_MISSING_FIELD: {
    codigo: 'MAPPING_MISSING_FIELD',
    httpStatus: 422, // ⇒ QUARENTENA do registro, nunca aplicacao parcial
    mensagemUsuario:
      'Alguns itens do sistema da loja estão incompletos e ficaram fora do catálogo.',
    acaoSugerida: 'BLOCK',
    grupo: 'CONECTOR',
    kind: 'ERRO',
  },
  MAPPING_UNRESOLVED_VALUE: {
    codigo: 'MAPPING_UNRESOLVED_VALUE',
    httpStatus: 422, // ⇒ quarentena agrupada pelo valor cru; resolve-se em lote
    mensagemUsuario: 'Alguns itens usam um valor que ainda não sabemos traduzir.',
    acaoSugerida: 'BLOCK',
    grupo: 'CONECTOR',
    kind: 'ERRO',
  },
  FILE_SHRINK_GUARD: {
    codigo: 'FILE_SHRINK_GUARD',
    httpStatus: 422, // protege contra exportacao truncada apagar o catalogo (limite de 70%)
    mensagemUsuario:
      'O arquivo enviado tem muito menos itens que o anterior. Não aplicamos — confira a exportação.',
    acaoSugerida: 'BLOCK',
    grupo: 'CONECTOR',
    kind: 'ERRO',
  },
} as const satisfies Record<string, ErrorCatalogEntry>;

/** Chave estavel do catalogo. E a unica coisa contra a qual o cliente programa (API §2.1). */
export type ErrorCode = keyof typeof ERROR_CATALOG;

/** A lista, na ordem do documento. Derivada do catalogo: nao existe segunda transcricao. */
export const ERROR_CODE_VALUES = Object.keys(ERROR_CATALOG) as [ErrorCode, ...ErrorCode[]];

export const ErrorCodeSchema = z.enum(ERROR_CODE_VALUES);

/** Numero contratual do catalogo fechado (API §10.3, CONTRACTS §7.3.1). */
export const ERROR_CATALOG_SIZE = 106 as const;
export const ERROR_CATALOG_ERROR_COUNT = 103 as const;
export const ERROR_CATALOG_WARNING_COUNT = 3 as const;

/**
 * Os 3 codigos que **recusam o boot da instancia** (FLEET §2.3). Nao tem resposta HTTP em
 * operacao normal: o `500` e o que a sonda externa observa enquanto o processo nao sobe.
 */
export const ERROR_CODES_BOOT_ONLY = [
  'ERP_DB_USER_NOT_READONLY',
  'ERP_ADAPTER_NOT_CERTIFIED',
  'ERP_PROFILE_REQUIRED',
] as const satisfies readonly ErrorCode[];

/**
 * De-para dos nomes **eliminados** (X-16 — CONTRACTS §7.3, API §2.3). Existe em codigo para que
 * o gate consiga apontar o substituto em vez de so dizer "codigo inexistente".
 *
 * A tabela do documento tem **12 linhas**; aqui sao 13 chaves porque a ultima linha lista dois
 * nomes (`PRICE_SNAPSHOT_EXPIRED` / `QUOTE_STALE`) colapsando no mesmo canonico.
 */
export const ELIMINATED_ERROR_CODE_MAP = {
  ORDER_ILLEGAL_TRANSITION: 'ORDER_TRANSITION_INVALID',
  CART_STORE_CONFLICT: 'CART_STORE_MISMATCH',
  LOYALTY_NEGATIVE_BALANCE: 'LOYALTY_INSUFFICIENT_BALANCE',
  DIVERGENCIA_DE_PACIENTE: 'PRESCRIPTION_PATIENT_MISMATCH',
  COUPON_MIN_SUBTOTAL: 'COUPON_MIN_VALUE_NOT_MET',
  COUPON_STORE_NOT_ELIGIBLE: 'COUPON_STORE_RESTRICTED',
  COUPON_EXHAUSTED: 'COUPON_USAGE_LIMIT_REACHED',
  COUPON_NOT_APPLICABLE_TO_CLASS: 'COUPON_NOT_APPLICABLE_ITEMS',
  PROMOTION_EXCLUSIVE: 'COUPON_NOT_CUMULATIVE',
  QUANTITY_LIMIT: 'QUANTITY_LIMIT_EXCEEDED',
  INSUFFICIENT_STOCK: 'ITEM_OUT_OF_STOCK',
  PRICE_SNAPSHOT_EXPIRED: 'QUOTE_EXPIRED',
  QUOTE_STALE: 'QUOTE_EXPIRED',
} as const satisfies Record<string, ErrorCode>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** `true` se a string e um codigo do catalogo fechado. */
export function isErrorCode(value: string): value is ErrorCode {
  return Object.prototype.hasOwnProperty.call(ERROR_CATALOG, value);
}

export function getErrorCatalogEntry(code: ErrorCode): ErrorCatalogEntry {
  return ERROR_CATALOG[code];
}

/** Codigos de um grupo do API §2.2. */
export function errorCodesByGroup(grupo: ErrorGroup): readonly ErrorCode[] {
  return ERROR_CODE_VALUES.filter((code) => ERROR_CATALOG[code].grupo === grupo);
}

/**
 * PENDENTE (aberto para a rodada de rotas): o unico `type` atestado nos documentos e o exemplo
 * `urn:farmacia:erro:item-sem-estoque` (API §2.1) — um slug em **pt-BR**, nao a transliteracao
 * do codigo. Nao existe tabela codigo -> slug em documento nenhum, e inventar 106 slugs em
 * portugues seria criar exatamente a segunda lista que a reconciliacao passou cinco rodadas
 * eliminando. Ate a tabela existir, o URN e o kebab-case do proprio codigo, que e estavel,
 * unico e derivavel dos dois lados. **Nao trate isto como decidido.**
 */
export function problemTypeUrn(code: ErrorCode): string {
  return `urn:farmacia:erro:${code.toLowerCase().replaceAll('_', '-')}`;
}

// ---------------------------------------------------------------------------
// RFC 9457 — problem details (API §2.1)
// ---------------------------------------------------------------------------

/** Erro de campo. So aparece em `422` de validacao. `pointer` e JSON Pointer (RFC 6901). */
export const ProblemFieldErrorSchema = z.object({
  pointer: z.string().min(1),
  code: z.string().min(1),
  detail: z.string().optional(),
});
export type ProblemFieldError = z.infer<typeof ProblemFieldErrorSchema>;

/**
 * `application/problem+json`.
 *
 * Nota de privacidade que vale mais que o schema: `detail` **nunca** contem nome de
 * medicamento em rota que toca saude — usa "um item do seu pedido", e o nome so aparece em
 * `meta.items[].displayName`, que a UI renderiza atras de autenticacao e que o pipeline de
 * telemetria descarta (REG-044).
 */
export const ProblemDetailsSchema = z.object({
  /** URN do tipo do problema. Ver a nota de `problemTypeUrn`. */
  type: z.string().min(1),
  /** Titulo para humanos. Pode mudar sem breaking change. */
  title: z.string().min(1),
  status: z.number().int().min(100).max(599),
  /** Detalhe para humanos. Sujeito a REG-044 nas rotas de saude. */
  detail: z.string().optional(),
  /** Caminho que produziu o erro. */
  instance: z.string().optional(),
  /** Extensao: codigo estavel de maquina. A unica coisa contra a qual o cliente programa. */
  code: ErrorCodeSchema,
  /** Extensao: correlacao com o log estruturado (ecoa `X-Request-Id`). */
  requestId: z.string().min(1),
  /** Extensao: erros de campo — so em `422` de validacao. */
  errors: z.array(ProblemFieldErrorSchema).optional(),
  /** Extensao: dados que o cliente precisa para se recuperar (`cartVersion`, `missingKey`, ...). */
  meta: z.record(z.string(), z.unknown()).optional(),
});
export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;

/**
 * Aviso transportado no corpo de uma resposta `200` — as 3 linhas `200*` do API §2.2.
 * Nao e `problem+json` e nao deve ser tratado como erro pela UI.
 */
export const WarningNoticeSchema = z.object({
  code: ErrorCodeSchema,
  message: z.string().min(1),
  meta: z.record(z.string(), z.unknown()).optional(),
});
export type WarningNotice = z.infer<typeof WarningNoticeSchema>;
